import { useEffect, useState } from "react";
import { themeApi } from "../../services/userApi";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Toggle } from "../../components/common/Toggle";
import { Button } from "../../components/common/Button";
import { Spinner } from "../../components/common/Spinner";
import styles from "./FeatureTogglesTab.module.css";

const PRESETS_KEY = "beekeeper.feature_presets";

function loadPresets() {
  try {
    return JSON.parse(localStorage.getItem(PRESETS_KEY) || "{}");
  } catch {
    return {};
  }
}

/**
 * Presets are stored in localStorage (per-browser, not synced across
 * devices) -- there is no backend table for saved feature-toggle presets
 * today. A cross-device version would need a small addition to
 * api/services/theme.py plus a table to hold named toggle sets per user.
 */
export function FeatureTogglesTab() {
  const { refresh: refreshTheme } = useFeatureTheme();
  const { notify } = useToast();
  const [features, setFeatures] = useState(null);
  const [presets, setPresets] = useState(loadPresets);
  const [presetName, setPresetName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    themeApi.getFeatures().then((r) => setFeatures(r.features));
  }, []);

  if (!features) return <Spinner />;

  const grouped = features.reduce((acc, f) => {
    (acc[f.category] ||= []).push(f);
    return acc;
  }, {});

  async function toggle(featureId, enabled) {
    setFeatures((prev) => prev.map((f) => (f.feature_id === featureId ? { ...f, enabled } : f)));
    try {
      await themeApi.updateFeatures([{ feature_id: featureId, enabled }]);
    } catch (err) {
      notify(err.apiMessage || "Could not save toggle", "danger");
    }
  }

  function savePreset() {
    if (!presetName.trim()) return;
    const next = { ...presets, [presetName.trim()]: features.map((f) => ({ feature_id: f.feature_id, enabled: f.enabled })) };
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
    setPresets(next);
    setPresetName("");
    notify(`Preset "${presetName.trim()}" saved`, "success");
  }

  async function loadPreset(name) {
    setSaving(true);
    try {
      await themeApi.updateFeatures(presets[name]);
      const r = await themeApi.getFeatures();
      setFeatures(r.features);
      notify(`Preset "${name}" applied`, "success");
    } catch (err) {
      notify(err.apiMessage || "Could not apply preset", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function resetToBasic() {
    setSaving(true);
    try {
      const { themeApi: api } = await import("../../services/userApi");
      await api.update("Basic");
      await refreshTheme();
      notify("Reset to Basic theme", "success");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <input id="feature-toggles-tab-preset-name" name="preset-name"
          className={styles.input}
          placeholder="Preset name"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
        />
        <Button size="sm" variant="secondary" onClick={savePreset}>Save Preset</Button>
        {Object.keys(presets).map((name) => (
          <Button key={name} size="sm" variant="secondary" disabled={saving} onClick={() => loadPreset(name)}>
            Load "{name}"
          </Button>
        ))}
        <Button size="sm" variant="danger" disabled={saving} onClick={resetToBasic}>Reset to Basic</Button>
      </div>

      {Object.entries(grouped).map(([category, items]) => (
        <Card key={category} title={category} className="section-spacing">
          <div className={styles.list}>
            {items.map((f) => (
              <Toggle
                key={f.feature_id}
                id={`feature-${f.feature_id}`}
                label={f.feature_name}
                checked={f.enabled}
                onChange={(v) => toggle(f.feature_id, v)}
              />
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
